import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import http from '@utils/http';
import { useEdocSessionStore } from '@stores/edocSession.store';
import { getEdocPublicMe, submitEdocPublic } from '@services/edocPublic.service';

async function getPublicEdocByNumber(edocNumber) {
    const res = await http.get(`/tenant/edocs-platform/public/${encodeURIComponent(edocNumber)}`);
    return res?.data?.data ?? null;
}

async function verifyEdocCustomer({ edocNumber, edocId, email, phone }) {
    const num = String(edocNumber || '').trim();
    const id = String(edocId || '').trim();

    // Prefer legacy route when an id is available; otherwise use by-number route (new payload).
    const url = id
        ? `/tenant/edocs-platform/public/${encodeURIComponent(id)}/verify-customer`
        : '/tenant/edocs-platform/public/verify-customer';

    const body = {
        edocNumber: num,
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {})
    };

    const res = await http.post(url, body);
    return res?.data?.data ?? null;
}

export const useEdocEntry = (edocNumberRaw) => {
    const queryClient = useQueryClient();
    const [isVerified, setIsVerified] = useState(false);
    const setSession = useEdocSessionStore((s) => s.setSession);
    const setMe = useEdocSessionStore((s) => s.setMe);
    const storedAccessToken = useEdocSessionStore((s) => s.accessToken);
    const storedMe = useEdocSessionStore((s) => s.me);
    const storedSessionEdocNumber = useEdocSessionStore((s) => s.edocNumber);
    const clearSession = useEdocSessionStore((s) => s.clear);

    const edocNumber = useMemo(() => String(edocNumberRaw || '').trim(), [edocNumberRaw]);
    const enabled = Boolean(edocNumber);

    // If tokens belong to a different eDoc than the current URL param, expire session immediately.
    useEffect(() => {
        if (!enabled) return;
        if (!storedAccessToken) return;
        const stored = String(storedSessionEdocNumber || '').trim();
        if (!stored) return;
        if (stored !== edocNumber) {
            clearSession();
            queryClient.removeQueries({ queryKey: ['edocPublicMe'], exact: true });
            // Keep publicEdoc query for current edocNumber intact; it will fetch latest for this URL.
            setIsVerified(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, edocNumber, storedAccessToken, storedSessionEdocNumber]);

    const publicEdocQuery = useQuery({
        queryKey: ['publicEdoc', edocNumber],
        enabled,
        queryFn: () => getPublicEdocByNumber(edocNumber),
        retry: 0
    });

    const verifyMutation = useMutation({
        mutationFn: async ({ edocNumber: num, edocId, email, phone }) =>
            verifyEdocCustomer({ edocNumber: num, edocId, email, phone }),
        onSuccess: (result) => {
            if (result?.tokens?.accessToken) {
                setSession(result.tokens, edocNumber);
            }
            if (result?.me) {
                setMe(result.me);
                queryClient.setQueryData(['edocPublicMe'], result.me);
            }
            if (result?.verified) setIsVerified(true);
        }
    });

    const submitMutation = useMutation({
        mutationFn: async ({ edocNumber: num, payload }) => submitEdocPublic(num, payload),
        onSuccess: (result) => {
            // Some backends may return updated public "me" payload after submit.
            if (result?.me) {
                setMe(result.me);
                queryClient.setQueryData(['edocPublicMe'], result.me);
            }
            queryClient.invalidateQueries({ queryKey: ['edocPublicMe'] });
            if (enabled) queryClient.invalidateQueries({ queryKey: ['publicEdoc', edocNumber] });
        }
    });

    const meQuery = useQuery({
        queryKey: ['edocPublicMe'],
        // Important: /public/me should only be used on "hard reload" token restore.
        // If we already have me in memory (Zustand) or React Query cache, don't refetch.
        enabled:
            Boolean(storedAccessToken) &&
            (!storedSessionEdocNumber || String(storedSessionEdocNumber).trim() === edocNumber) &&
            !storedMe &&
            !queryClient.getQueryData(['edocPublicMe']),
        queryFn: async () => {
            const me = await getEdocPublicMe();
            setMe(me);
            return me;
        },
        onSuccess: (me) => {
            if (me) {
                queryClient.setQueryData(['edocPublicMe'], me);
                setIsVerified(true);
            }
        },
        retry: 0
    });

    const publicEdoc = publicEdocQuery.data || null;
    const publicEdocErrorStatus = publicEdocQuery.error?.response?.status;
    const isNotFound = enabled && publicEdocQuery.isError && publicEdocErrorStatus === 404;
    const isFetchFailed = enabled && publicEdocQuery.isError && !isNotFound;

    const cachedPublicEdoc =
        storedMe
        || queryClient.getQueryData(['edocPublicMe'])
        || meQuery.data
        || (enabled ? queryClient.getQueryData(['publicEdoc', edocNumber]) : null);

    return {
        edocNumber,
        enabled,

        publicEdocQuery,
        publicEdoc,
        cachedPublicEdoc,
        meQuery,
        isNotFound,
        isFetchFailed,

        isVerified,
        setIsVerified,

        verifyCustomer: verifyMutation.mutateAsync,
        verifying: verifyMutation.isPending,

        submitEdoc: submitMutation.mutateAsync,
        submitting: submitMutation.isPending
    };
};

